import { motion } from "framer-motion";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { Section } from "@/components/layout/Section";
import { Container } from "@/components/layout/Container";
import { PricingCard } from "@/components/pricing/PricingCard";
import { AnimatedSection } from "@/components/ui/animated-section";
import { staggerContainer, staggerItem } from "@/lib/animations";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";

const pricingPlans = [
  {
    name: "Free",
    price: "$0",
    description: "Perfect for trying out",
    features: [
      "3 surveys per month",
      "100 responses per survey",
      "Basic question types",
      "Email support",
      "Export to CSV",
    ],
    popular: false,
  },
  {
    name: "Pro",
    price: "$29",
    description: "For growing teams",
    features: [
      "Unlimited surveys",
      "1,000 responses per survey",
      "All question types",
      "Priority support",
      "Advanced analytics",
      "Custom branding",
    ],
    popular: true,
  },
  {
    name: "Enterprise",
    price: "Custom",
    description: "For large organizations",
    features: [
      "Unlimited everything",
      "API access",
      "SSO/SAML integration",
      "Dedicated support",
      "Custom integrations",
      "SLA guarantee",
    ],
    popular: false,
  },
];

export default function Pricing() {
  useDocumentMeta({
    title: 'Pricing',
    description: 'Simple, transparent pricing. Start free with 3 surveys. Upgrade to Pro for unlimited surveys and advanced features.'
  });

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main>
        <Section className="pt-24 md:pt-32">
          <Container>
            <AnimatedSection className="text-center mb-12 md:mb-16">
              <h1 className="text-4xl md:text-5xl font-bold text-foreground">
                Simple, <span className="text-gradient">Transparent</span> Pricing
              </h1>
              <p className="mt-4 text-lg text-muted-foreground max-w-2xl mx-auto">
                Start free, upgrade when you need more. No hidden fees, no surprises.
              </p>
            </AnimatedSection>
            
            {/* Responsive: stack on mobile, 3 columns on desktop */}
            <motion.div 
              className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 max-w-5xl mx-auto"
              variants={staggerContainer}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
            >
              {pricingPlans.map((plan) => (
                <motion.div key={plan.name} variants={staggerItem}>
                  <PricingCard
                    name={plan.name}
                    price={plan.price}
                    description={plan.description}
                    features={plan.features}
                    popular={plan.popular}
                  />
                </motion.div>
              ))}
            </motion.div>
            
            {/* FAQ teaser */}
            <AnimatedSection className="text-center mt-16" delay={0.3}>
              <p className="text-muted-foreground">
                Have questions? Check out our{" "}
                <a href="/#faq" className="text-primary hover:underline">
                  FAQ section
                </a>{" "}
                or{" "}
                <a href="/contact" className="text-primary hover:underline">
                  contact us
                </a>
                .
              </p>
            </AnimatedSection>
          </Container>
        </Section>
      </main>

      <Footer />
    </div>
  );
}
